<template>
  <div>
    <advanced-table
      :headers="headers"
      :filters="filters"
      :sortBy="'callFirst'"
      :descending="true"
      :resource="resource"
      :optionLists="optionLists"
      @list-updated="onPartnersListUpdated"
    />
    <partner-check-dialog
      v-model="checkDialog"
      :partner="selectedPartner"
      @checked="onPartnerChecked"
    />
    <partner-check-chat-dialog
      v-model="chatDialog"
      :partner="selectedPartner"
      @updated="onChatUpdated"
    />
  </div>
</template>

<script>
import AdvancedTable from "@/components/AdvancedTable.vue";
import PartnerCheckDialog from "@/components/events/PartnerCheckDialog.vue";
import PartnerCheckChatDialog from "@/components/partners/PartnerCheckChatDialog.vue";
import { getApi } from "@/api";
import { format, differenceInCalendarDays } from "date-fns";

export default {
  data() {
    return {
      checkDialog: false,
      chatDialog: false,
      selectedPartner: null,
      messageCounts: {},
      unreadCounts: {},
      headers: [
        {
          text: "שם",
          value: "name",
          align: "right",
          visible: true,
          width: "200",
          displayFunc: (data, key) => {
            return data[key] || 'ללא שם';
          },
          linkTo: item => {
            return `/partners/${item._id}`;
          }
        },
        {
          text: "תאריך יצירה",
          value: "created",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            return format(data[key], "DD-MM-YYYY");
          }
        },
        {
          text: "סוגים",
          value: "types",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "title";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  return this.entityById("partnerTypes", item)[field];
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "";
          }
        },
        {
          text: "מדינות",
          value: "countries",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "name";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  let country = this.entityById("countries", item)
                  return country ? country[field] : 'ללא שם';
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "";
          }
        },
        {
          text: "איזורים",
          value: "regions",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            let field = "name";
            let p = {};
            let array = data[key];
            if (Array.isArray(array) && array.length) {
              return array
                .map(item => {
                  return this.entityById("regions", item)[field];
                })
                .reduce((p, v) => p + ", " + v);
            }
            return "כל האיזורים";
          }
        },
        {
          text: "תאריך אימות פרטים",
          value: "lastValidation",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            return format(data[key], "DD-MM-YYYY");
          }
        },
        {
          text: "בדיקה",
          value: "checkDays",
          sortValue: "lastValidation",
          align: "right",
          visible: true,
          type: "CLICK",
          displayFunc: (data) => {
            return this.getCheckLabel(data);
          },
          click: (partner) => {
            this.openCheckDialog(partner);
          }
        },
        {
          text: "צ'אט",
          value: "checkMessageCount",
          align: "right",
          visible: true,
          type: "CLICK",
          clientSort: true,
          getSortKey: (partner) => {
            const count = this.messageCounts[partner._id];
            if (count === undefined || count === null) {
              return -1;
            }
            return count;
          },
          displayFunc: (data) => {
            const count = this.messageCounts[data._id];
            if (count === undefined || count === null) {
              return "—";
            }
            return count > 0 ? String(count) : "0";
          },
          hasUnread: (partner) => {
            return (this.unreadCounts[partner._id] || 0) > 0;
          },
          click: (partner) => {
            this.openChatDialog(partner);
          }
        },
        {
          text: "סוג הקשר",
          value: "relationship",
          align: "right",
          visible: false,
          displayFunc: (data, key) => {
            let relationship = this.entityById("partnerRelationships", data[key]);
            return relationship ? relationship.title : 'לא ידוע';
          }
        },
        {
          text: "מומלץ",
          value: "recommended",
          align: "right",
          visible: true,
          type: 'ICON',
          displayFunc: (data, key) => {
            return data[key] ? { icon: 'star', color: 'orange' } : { icon: 'mdi-star-outline', color: 'gray' };
          }
        },
        {
          text: "להתקשר ראשון",
          value: "callFirst",
          align: "right",
          visible: true,
          displayFunc: (data, key) => {
            return data[key] ? "כן" : "לא";
          }
        }
      ],
      filters: [
        {
          type: "COMBOBOX",
          label: "מדינות",
          model: "countries",
          items: "countries",
          itemText: "name",
          itemValue: "_id",
          active: true
        },
        {
          type: "COMBOBOX",
          label: "סוגי ספקים",
          model: "types",
          items: "types",
          itemText: "titlePlural",
          itemValue: "_id",
          active: true
        },
        {
          type: "DATE",
          label: "נוצר אחרי",
          model: "createdAfter",
          active: false
        },
        {
          type: "DATE",
          label: "נוצר לפני",
          model: "createdBefore",
          active: false
        },
        {
          type: "DATE",
          label: "אימות פרטים לפני",
          model: "lastValidationBefore",
          active: false
        },
        {
          type: "TEXT",
          label: "חיפוש חופשי",
          model: "search",
          icon: "search",
          expanded: true,
          active: true
        },
        {
          type: "BOOLEAN",
          label: "הצג רק ספקים עם הודעות שלא נקראו",
          model: "unreadCheckMessagesOnly",
          active: true
        }
      ]
    };
  },
  computed: {
    partners() {
      return this.$store.getters["entityList"]("partners");
    },
    relationships() {
      return this.$store.getters["entityList"]("partnerRelationships");
    },
    types() {
      return this.$store.getters["entityList"]("partnerTypes");
    },
    countries() {
      return this.$store.getters["entityList"]("countries").filter(item => {
        return item.isActive;
      });
    },
    resource() {
      return this.$store.state.resources["partner"];
    },
    optionLists() {
      return {
        relationships: this.relationships,
        types: this.types,
        countries: this.countries
      }
    }
  },
  methods: {
    entityById(entityType, id) {
      return this.$store.getters["entity"](entityType, id);
    },
    getCheckLabel(partner) {
      if (!partner || !partner.lastValidation) {
        return "אף פעם";
      }
      const days = differenceInCalendarDays(
        new Date(),
        new Date(partner.lastValidation)
      );
      if (isNaN(days)) {
        return "אף פעם";
      }
      return String(Math.max(0, days));
    },
    openCheckDialog(partner) {
      this.selectedPartner = partner;
      this.checkDialog = true;
    },
    openChatDialog(partner) {
      this.selectedPartner = partner;
      this.chatDialog = true;
    },
    onPartnersListUpdated(list) {
      this.fetchMessageCounts(list);
    },
    async fetchMessageCounts(partnerIds) {
      const ids = (partnerIds || [])
        .map(id => (typeof id === "string" ? id : id && id._id))
        .filter(Boolean);
      if (!ids.length) return;
      try {
        const api = await getApi();
        const { data } = await api.get("/partners/check-message-counts", {
          params: { ids: ids.join(",") }
        });
        if (data && data.counts) {
          this.messageCounts = { ...this.messageCounts, ...data.counts };
        }
        if (data && data.unreadCounts) {
          this.unreadCounts = { ...this.unreadCounts, ...data.unreadCounts };
        }
      } catch (err) {
        console.error(err);
      }
    },
    onPartnerChecked() {
      this.$forceUpdate();
    },
    onChatUpdated(partnerId) {
      if (partnerId) {
        this.$set(this.unreadCounts, partnerId, 0);
        this.fetchMessageCounts([partnerId]);
      }
    },
    onPartnerCheckMessageSocket(payload) {
      if (!payload || !payload.partnerId) return;
      const id = payload.partnerId;
      const prev = this.messageCounts[id];
      this.messageCounts = {
        ...this.messageCounts,
        [id]: (typeof prev === "number" ? prev : 0) + 1
      };
      const msg = payload.message;
      const isInbound = !msg || msg.direction === "inbound";
      if (isInbound) {
        const chatOpenForPartner =
          this.chatDialog &&
          this.selectedPartner &&
          this.selectedPartner._id === id;
        if (chatOpenForPartner) {
          this.$set(this.unreadCounts, id, 0);
        } else {
          const unreadPrev = this.unreadCounts[id];
          this.$set(
            this.unreadCounts,
            id,
            (typeof unreadPrev === "number" ? unreadPrev : 0) + 1
          );
        }
      }
    }
  },
  sockets: {
    partner_check_message(payload) {
      this.onPartnerCheckMessageSocket(payload);
    }
  },
  components: {
    AdvancedTable,
    PartnerCheckDialog,
    PartnerCheckChatDialog
  }
};
</script>



// WEBPACK FOOTER //
// src/components/partners/Partners.vue